import SearchOutlined from '@ant-design/icons/SearchOutlined'
import { Card, DatePicker, Input, TimeRangePickerProps } from 'antd'
import dayjs from 'dayjs'
import { useOrgSite } from 'features/fde'
import { INBOX_DARK_BACKGROUND } from 'features/inbox/colors'
import { DRALevelFilter } from 'features/inbox/components/filters/DRALevelFilter'
import {
  FilterSection,
  FilterTitle,
} from 'features/inbox/components/filters/filterElements'
import { InspectionStatusFilter } from 'features/inbox/components/filters/InspectionStatusFilter'
import { InspectionTypeFilter } from 'features/inbox/components/filters/InspectionTypeFilter'
import { useInbox } from 'features/inbox/contexts/InboxContext'
import { useInspectionData } from 'features/inbox/hooks'
import {
  EnrichedGeckoInspection,
  InspectionStatus,
} from 'features/inbox/types/types'
import { applyInboxFilters } from 'features/inbox/utils/filterInspections'
import debounce from 'lodash/debounce'
import groupBy from 'lodash/groupBy'
import mapValues from 'lodash/mapValues'
import React, { memo, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

const FilterSidebarContainer = styled(Card)`
  height: 100%;
  border: 0;
  border-right: 1px solid rgba(90, 90, 90, 1);
  border-radius: 0;
  background: ${INBOX_DARK_BACKGROUND};
  display: flex;
  flex-direction: column;
  .ant-card-body {
    padding: 1rem;
    overflow-y: auto;
    flex: 1;
  }
`

const rangePresets: TimeRangePickerProps['presets'] = [
  { label: 'Next 30 Days', value: [dayjs(), dayjs().add(30, 'd')] },
  { label: 'Next 14 Days', value: [dayjs(), dayjs().add(14, 'd')] },
  { label: 'Last 7 Days', value: [dayjs().add(-7, 'd'), dayjs()] },
  { label: 'Last 14 Days', value: [dayjs().add(-14, 'd'), dayjs()] },
  { label: 'Last 30 Days', value: [dayjs().add(-30, 'd'), dayjs()] },
  { label: 'Last 90 Days', value: [dayjs().add(-90, 'd'), dayjs()] },
  { label: 'Last 180 Days', value: [dayjs().add(-180, 'd'), dayjs()] },
]

const InboxFilterSidebarComponent: React.FC = () => {
  const { state, setFilters } = useInbox()
  const { siteId, orgId } = useOrgSite()

  const [searchBarText, setSearchBarText] = useState(state.filters.searchText)

  const debouncedSetSearchFilter = useMemo(
    () => debounce((text: string) => setFilters({ searchText: text }), 300),
    [setFilters]
  )

  const { inspections: allInspections } = useInspectionData({
    siteId: siteId ?? undefined,
    orgId: orgId ?? undefined,
    sortDirection: state.sortDirection,
    filters: {
      statusFilters: [],
      inspectionTypeFilters: [],
      draLevelFilters: [],
      searchText: '',
      showFutureInspections: false,
    },
  })

  const handleStatusFilterChange = useCallback(
    (checkedValues: ReadonlyArray<string>) => {
      setFilters({ statusFilters: checkedValues as InspectionStatus[] })
    },
    [setFilters]
  )

  const handleTypeFilterChange = useCallback(
    (checkedValues: ReadonlyArray<string>) => {
      setFilters({ inspectionTypeFilters: checkedValues })
    },
    [setFilters]
  )

  const handleDRALevelFilterChange = useCallback(
    (checkedValues: ReadonlyArray<number>) => {
      setFilters({ draLevelFilters: checkedValues })
    },
    [setFilters]
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchBarText(value)
      debouncedSetSearchFilter(value)
    },
    [debouncedSetSearchFilter]
  )

  const handleDateRangeChange = useCallback(
    (values: ReadonlyArray<dayjs.Dayjs> | null) => {
      if (!values || values.length !== 2 || !values[0] || !values[1]) {
        setFilters({ dateRange: undefined })
        return
      }
      setFilters({
        dateRange: {
          start: values[0].startOf('day').toISOString(),
          end: values[1].endOf('day').toISOString(),
        },
      })
    },
    [setFilters]
  )

  const statusCounts = useMemo(() => {
    const filteredInspections = applyInboxFilters(
      allInspections,
      state.filters,
      {
        excludeFilters: ['status'],
      }
    )
    const groupedInspections = groupBy(
      filteredInspections,
      (inspection) => inspection.data.derivedStatus
    )
    return mapValues(groupedInspections, (arr) => arr.length)
  }, [allInspections, state.filters])

  const typeCounts = useMemo(() => {
    const filteredInspections = applyInboxFilters(
      allInspections,
      state.filters,
      {
        excludeFilters: ['type'],
      }
    )
    const groupedInspections = groupBy(
      filteredInspections,
      (inspection) => inspection.data?.inspection_type
    )
    return mapValues(groupedInspections, (arr) => arr.length)
  }, [allInspections, state.filters])

  const draCounts = useMemo(() => {
    const filteredInspections = applyInboxFilters(
      allInspections,
      state.filters,
      {
        excludeFilters: ['dra'],
      }
    )
    const filteredInspectionsWithWorstDRA = (
      filteredInspections as ReadonlyArray<EnrichedGeckoInspection>
    ).filter((inspection) => inspection.data.worstDRA !== undefined)

    const inspectionsGroupedByWorstDRA = groupBy(
      filteredInspectionsWithWorstDRA,
      (inspection) => inspection.data.worstDRA
    )

    return mapValues(inspectionsGroupedByWorstDRA, (arr) => arr.length)
  }, [allInspections, state.filters])

  return (
    <FilterSidebarContainer>
      <FilterSection>
        <FilterTitle level={5}>Search</FilterTitle>
        <Input
          placeholder='Search...'
          prefix={<SearchOutlined />}
          value={searchBarText}
          onChange={(e) => handleSearchChange(e.target.value)}
          allowClear
        />
      </FilterSection>

      <InspectionStatusFilter
        selectedStatusFilters={state.filters.statusFilters}
        countsByStatusKey={statusCounts}
        onChange={handleStatusFilterChange}
      />

      <InspectionTypeFilter
        selectedTypeFilters={state.filters.inspectionTypeFilters}
        countsByTypeKey={typeCounts}
        onChange={handleTypeFilterChange}
      />

      <DRALevelFilter
        selectedDRAFilters={state.filters.draLevelFilters}
        countsByDRALevel={draCounts}
        onChange={handleDRALevelFilterChange}
      />

      <FilterSection>
        <FilterTitle level={5}>Date range</FilterTitle>
        <DatePicker.RangePicker
          value={
            state.filters.dateRange
              ? [
                  dayjs(state.filters.dateRange.start),
                  dayjs(state.filters.dateRange.end),
                ]
              : undefined
          }
          onChange={(vals) =>
            handleDateRangeChange((vals as ReadonlyArray<dayjs.Dayjs>) || null)
          }
          presets={rangePresets}
          allowEmpty={[true, true]}
          allowClear
        />
      </FilterSection>
    </FilterSidebarContainer>
  )
}

export const InboxFilterSidebar = memo(InboxFilterSidebarComponent)
